import { useCallback, useState } from 'react'
import { useMount } from '~/shared/hooks/useMount'
import type { FC } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import SearchIcon from '@mui/icons-material/Search'
import { TextField, InputAdornment, Box, FormControlLabel, Grid } from '@mui/material'
import { CPTCode } from './types'

import { LocationTypeSearchCategory, SpecialtyGroupSearchCategory } from './ProviderSearchContent'
import { EditField } from '~/shared/ui'
import { Checkbox } from '@mui/material'
import {
  ClinicalFocusArea,
  ClinicalFocusAreaAutocomplete,
  SpecialtyDropdownOption,
} from '~/entities/steerage'
interface SpecialtyGroupSearchOptionsPickerProps {
  specialtyGroups: SpecialtyDropdownOption[]
  cptCodes: CPTCode[]
  onSelect: (
    locationType: LocationTypeSearchCategory | null,
    specialtyGroup: SpecialtyGroupSearchCategory | null,
    npi: string | null,
    providerName: string | null,
    gender: string | null,
    facilityName: string | null,
    procedureCode: CPTCode | null,
    cptCode: CPTCode | null,
    clinicalFocusAreas?: ClinicalFocusArea[] | [],
  ) => void
  disabled: boolean
  loading: boolean
  selectedSpecialtyGroup: SpecialtyGroupSearchCategory | null
  selectedNPI: string | null
  selectedProviderName: string | null
  selectedGender: string | null
  selectedProcedureCode: CPTCode | null
  setSearchRadius: (val: number) => void
  selectedClinicalFocusAreas: ClinicalFocusArea[]
}

// Set gender to default F for OBGYN
export const SPECIALTY_TO_GENDER: Record<string, string | undefined> = {
  '(OBGYN) Obstetrics and Gynecology': 'F',
}

export const ProviderSearchOptionsPicker: FC<SpecialtyGroupSearchOptionsPickerProps> = (
  props: SpecialtyGroupSearchOptionsPickerProps,
) => {
  const [specialtyGroup, setSpecialtyGroup] = useState<SpecialtyGroupSearchCategory | null>(
    props.selectedSpecialtyGroup,
  )
  const [selectedClinicalFocusAreas, setSelectedClinicalFocusAreas] = useState<ClinicalFocusArea[]>(
    props.selectedClinicalFocusAreas,
  )
  const [searchString, setSearchString] = useState<string>('')
  const [cptCodeSearchString, setCPTCodeSearchString] = useState<string>('')
  const [npi, setNPI] = useState<string | null>(props.selectedNPI)
  const [providerName, setProviderName] = useState<string | null>(props.selectedProviderName)
  const [procedureCode, setProcedureCode] = useState<CPTCode | null>(props.selectedProcedureCode)
  const { selectedGender, onSelect } = props
  const isFemale = selectedGender == null || selectedGender === 'F'
  const isMale = selectedGender == null || selectedGender === 'M'

  // Should clinical focus area UI be shown?
  const shouldShowClinicalFocusAreaUI =
    specialtyGroup?.clinical_focus_areas && specialtyGroup.clinical_focus_areas.length > 0

  const handleCheckbox = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newGender: string | null = null
      if (selectedGender == null) newGender = e.target.value === 'F' ? 'M' : 'F'
      onSelect(
        null,
        specialtyGroup,
        npi,
        providerName,
        newGender,
        null,
        procedureCode,
        null,
        selectedClinicalFocusAreas,
      )
    },
    [
      selectedGender,
      onSelect,
      specialtyGroup,
      npi,
      providerName,
      procedureCode,
      selectedClinicalFocusAreas,
    ],
  )

  let specialtyGroupLabels: SpecialtyGroupSearchCategory[] = props.specialtyGroups?.map(
    specialtyGroup => {
      return {
        id: specialtyGroup.id,
        label: specialtyGroup.label,
        type: 'SpecialtyGroup',
        category: 'Specialty',
        clinical_focus_areas: specialtyGroup.clinical_focus_areas,
      }
    },
  )
  specialtyGroupLabels = specialtyGroupLabels || []

  const setSearchTerms = (
    event: React.SyntheticEvent,
    newValue: SpecialtyGroupSearchCategory | null,
  ) => {
    let specialtyGroupFromValue: SpecialtyGroupSearchCategory | null = null
    if (newValue?.type === 'SpecialtyGroup') {
      specialtyGroupFromValue = newValue
    }
    setSpecialtyGroup(specialtyGroupFromValue)

    // Clear clinical focus areas when specialty group changes
    // Only keep existing focus areas if the new specialty is the same as the current one
    const shouldClearFocusAreas = specialtyGroupFromValue?.id !== specialtyGroup?.id
    const clinicalFocusAreasToUse = shouldClearFocusAreas ? [] : selectedClinicalFocusAreas

    if (shouldClearFocusAreas) {
      setSelectedClinicalFocusAreas([])
    }

    onSelect(
      null,
      specialtyGroupFromValue,
      npi,
      providerName,
      specialtyGroupFromValue != null
        ? (SPECIALTY_TO_GENDER[specialtyGroupFromValue.label] ?? null)
        : selectedGender,
      null,
      procedureCode,
      null,
      clinicalFocusAreasToUse,
    )
  }
  const setSearchTermsForProcedureSearch = (
    event: React.SyntheticEvent,
    newValue: CPTCode | null,
  ) => {
    let codeFromValue: CPTCode | null = null
    if (newValue != null) {
      codeFromValue = newValue
    }
    setProcedureCode(codeFromValue)
    onSelect(
      null,
      specialtyGroup,
      npi,
      providerName,
      selectedGender,
      null,
      codeFromValue,
      null,
      selectedClinicalFocusAreas,
    )
  }

  const setNPIParam = (event: React.ChangeEvent<HTMLInputElement>) => {
    const npi = event.target.value
    setNPI(npi)
    onSelect(
      null,
      specialtyGroup,
      npi,
      providerName,
      selectedGender,
      null,
      procedureCode,
      null,
      selectedClinicalFocusAreas,
    )
  }

  const setProviderNameParam = (event: React.ChangeEvent<HTMLInputElement>) => {
    const providerNameFromEvent = event.target.value
    setProviderName(providerNameFromEvent)
    onSelect(
      null,
      specialtyGroup,
      npi,
      providerNameFromEvent,
      selectedGender,
      null,
      procedureCode,
      null,
      selectedClinicalFocusAreas,
    )

    // set distance to 25miles if it's name search else 10miles for other searches
    props?.setSearchRadius(providerNameFromEvent ? 25 : 10)
  }

  // Publishes the initial selection once, so the parent can seed a search from the URL. Every
  // later change already calls `onSelect` from the picker handler that made it.
  useMount(() => {
    onSelect(
      null,
      specialtyGroup,
      npi,
      providerName,
      selectedGender,
      null,
      procedureCode,
      null,
      selectedClinicalFocusAreas,
    )
  })

  return (
    <Box style={{ gap: 20 }} sx={{ display: 'flex', maxWidth: 640, flexDirection: 'column' }}>
      <Grid size={6}>
        <Autocomplete
          value={specialtyGroup}
          onChange={setSearchTerms}
          inputValue={searchString}
          onInputChange={(event, newSearchString) => {
            setSearchString(newSearchString)
          }}
          id="specialty-group-picker"
          options={specialtyGroupLabels}
          getOptionLabel={option => `${option.label}`}
          sx={{ width: '100%' }}
          size="small"
          disabled={props.disabled}
          loading={props.loading}
          forcePopupIcon={false}
          renderOption={({ key: _, ...props }, option: SpecialtyGroupSearchCategory) => (
            <li {...props} key={option.id}>
              {option.label}
            </li>
          )}
          renderInput={params => {
            return (
              <TextField
                {...params}
                label="Specialty"
                variant="outlined"
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )
          }}
        />
      </Grid>
      {shouldShowClinicalFocusAreaUI && (
        <Grid size={6}>
          <ClinicalFocusAreaAutocomplete
            clinicalFocusAreas={specialtyGroup?.clinical_focus_areas ?? []}
            onChangeClinicalFocusArea={value => {
              const newClinicalFocusAreas: ClinicalFocusArea[] = []
              value.forEach(v => {
                const found = specialtyGroup?.clinical_focus_areas?.find(s => s.id === v.id)
                if (found) newClinicalFocusAreas.push(found)
              })
              onSelect(
                null,
                specialtyGroup,
                npi,
                providerName,
                selectedGender,
                null,
                procedureCode,
                null,
                newClinicalFocusAreas,
              )
              setSelectedClinicalFocusAreas(newClinicalFocusAreas)
            }}
            selectedClinicalFocusAreas={selectedClinicalFocusAreas}
            textFieldVariant="outlined"
          />
        </Grid>
      )}
      <Grid size={6} sx={{ display: 'flex', alignItems: 'center' }}>
        <FormControlLabel
          control={<Checkbox value="F" checked={isFemale} onChange={handleCheckbox} />}
          label="Female Providers"
        />
        <FormControlLabel
          control={<Checkbox value="M" checked={isMale} onChange={handleCheckbox} />}
          label="Male Providers"
        />
      </Grid>
      <Grid size={6}>
        <Autocomplete
          value={procedureCode}
          onChange={setSearchTermsForProcedureSearch}
          inputValue={cptCodeSearchString}
          onInputChange={(event, newSearchString) => {
            setCPTCodeSearchString(newSearchString)
          }}
          id="cpt-code-picker"
          options={props.cptCodes || []}
          getOptionLabel={option => `${option.code} - ${option.description}`}
          sx={{ width: '100%' }}
          size="small"
          disabled={props.disabled}
          loading={props.loading}
          forcePopupIcon={false}
          renderOption={({ key: _, ...props }, option: CPTCode) => (
            <li {...props} key={option.id}>
              {option.code} - {option.description}
            </li>
          )}
          renderInput={params => {
            return (
              <TextField
                {...params}
                label="Procedure Code"
                variant="outlined"
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )
          }}
        />
      </Grid>
      <EditField
        label="Provider Name"
        type="text"
        name="providerName"
        value={providerName}
        onChange={setProviderNameParam}
        disabled={props.disabled}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <EditField
        label="NPI"
        type="number"
        name="npi"
        value={npi}
        onChange={setNPIParam}
        disabled={props.disabled}
        slotProps={{ inputLabel: { shrink: true } }}
      />
    </Box>
  )
}
